/**
 * FeedSettingsSection — Feed Video Level, Dialect, Daily Listening Goal, Video
 * Topics. Web port of client-native LearningSettingsScreen + its selectors.
 * Backing fields already exist in UserSettings; round-tripped via useUserSettings.
 */
import { FC, useMemo, useState } from "react";
import { colors } from "@/ui";
import type { CEFRLevel, UserSettings } from "@shared_types";
import { useUserSettings } from "@/hooks/useUserSettings";
import Stepper from "./Stepper";
import HelpTooltip from "./HelpTooltip";
import DialectFlag from "./DialectFlag";
import FeedTopicsModal from "./FeedTopicsModal";

const LEVEL_OPTIONS: { cefr: CEFRLevel; displayName: string; color: string }[] = [
  { cefr: "A0", displayName: "Super Beginner", color: colors.green },
  { cefr: "Lower A1", displayName: "Beginner", color: colors.green },
  { cefr: "Upper A2", displayName: "Upper Beginner", color: colors.blue },
  { cefr: "Lower B1", displayName: "Intermediate", color: colors.accent },
  { cefr: "Upper B2", displayName: "Upper Intermediate", color: colors.orange },
  { cefr: "Lower C1", displayName: "Advanced", color: colors.red },
];

const GOAL_OPTIONS = [
  { minutes: 5, displayName: "Light", description: "5 min/day" },
  { minutes: 10, displayName: "Moderate", description: "10 min/day" },
  { minutes: 15, displayName: "Committed", description: "15 min/day" },
  { minutes: 20, displayName: "Intense", description: "20 min/day" },
  { minutes: 25, displayName: "Hardcore", description: "25 min/day" },
];

type Dialect = "spain" | "latam" | "unknown";
const DIALECT_OPTIONS: { value: Dialect; label: string; kind: "spain" | "latam" | "both" }[] = [
  { value: "spain", label: "Spain", kind: "spain" },
  { value: "latam", label: "LatAm", kind: "latam" },
  { value: "unknown", label: "Both", kind: "both" },
];

const SectionDivider: FC = () => (
  <div style={{ height: 1, backgroundColor: colors.greyLightest, margin: "16px 0" }} />
);

const FeedSettingsSection: FC = () => {
  const { settings, updateSettings } = useUserSettings();
  const [topicsOpen, setTopicsOpen] = useState(false);

  const levelIndex = useMemo(() => {
    const lvl = settings?.userFeedLevel;
    if (!lvl || lvl === "No Level") return 0;
    const i = LEVEL_OPTIONS.findIndex((o) => o.cefr === lvl);
    return i >= 0 ? i : 0;
  }, [settings?.userFeedLevel]);

  const goalIndex = useMemo(() => {
    const i = GOAL_OPTIONS.findIndex((o) => o.minutes === settings?.dailyListeningGoalMinutes);
    return i >= 0 ? i : 0;
  }, [settings?.dailyListeningGoalMinutes]);

  const dialect = (settings?.userFeedDialect as Dialect) ?? "unknown";
  const levelOption = LEVEL_OPTIONS[levelIndex];
  const goalOption = GOAL_OPTIONS[goalIndex];
  const selectedTopicCount = settings?.userFeedTags?.length ?? 0;

  // iOS's updateUserSettings never rejects — it catches internally and rolls
  // back the optimistic value (globalStore.ts:275-305). The web hook rejects on
  // failure but already rolls back + logs in onError, so swallow the rejection
  // here to avoid an unhandled promise rejection while keeping that parity.
  const persist = (patch: Partial<UserSettings>) => {
    void updateSettings(patch).catch(() => {});
  };

  const setLevel = (i: number) =>
    persist({ userFeedLevel: LEVEL_OPTIONS[i].cefr } as Partial<UserSettings>);
  const setGoal = (i: number) =>
    persist({ dailyListeningGoalMinutes: GOAL_OPTIONS[i].minutes });

  return (
    <div>
      {/* Feed Video Level */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <span className="text-base" style={{ color: colors.primary }}>Feed Video Level</span>
        <HelpTooltip
          title="Feed Video Level"
          content='This controls the difficulty of the "Feed" tab in the bottom bar.'
        />
      </div>
      <p className="text-sm" style={{ color: colors.secondary, marginTop: 4, marginBottom: 8 }}>
        Select the difficulty level of videos you want to see in your feed.
      </p>
      <Stepper
        canDecrease={levelIndex > 0}
        canIncrease={levelIndex < LEVEL_OPTIONS.length - 1}
        onDecrease={() => setLevel(levelIndex - 1)}
        onIncrease={() => setLevel(levelIndex + 1)}
      >
        <div
          className="text-base font-bold"
          style={{
            backgroundColor: levelOption.color,
            color: colors.white,
            padding: "16px 8px",
            borderRadius: 12,
            textAlign: "center",
          }}
        >
          {levelOption.displayName}
        </div>
      </Stepper>

      <SectionDivider />

      {/* Feed Video Dialect */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <span className="text-base" style={{ color: colors.primary }}>Feed Video Dialect</span>
        <HelpTooltip
          title="Feed Video Dialect"
          content='Choose between Spain Spanish or Latin American Spanish for videos in your "Feed" tab.'
        />
      </div>
      <p className="text-sm" style={{ color: colors.secondary, marginTop: 4, marginBottom: 8 }}>
        Tap to switch dialect. Your feed will update automatically.
      </p>
      <div style={{ display: "flex", gap: 10 }}>
        {DIALECT_OPTIONS.map((opt) => {
          const selected = dialect === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => persist({ userFeedDialect: opt.value } as Partial<UserSettings>)}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "10px 4px",
                borderRadius: 12,
                border: `1.5px solid ${selected ? colors.blue : colors.greyLight}`,
                backgroundColor: selected ? colors.blue : colors.white,
                cursor: "pointer",
              }}
            >
              <DialectFlag kind={opt.kind} />
              <span
                className="text-sm"
                style={{
                  color: selected ? colors.white : colors.primary,
                  fontWeight: selected ? 700 : 400,
                }}
              >
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>

      <SectionDivider />

      {/* Daily Listening Goal */}
      <span className="text-base font-bold" style={{ color: colors.primary }}>
        Daily Listening Goal
      </span>
      <p className="text-sm" style={{ color: colors.secondary, marginTop: 4, marginBottom: 8 }}>
        How many minutes of listening per day to hit your streak goal.
      </p>
      <Stepper
        canDecrease={goalIndex > 0}
        canIncrease={goalIndex < GOAL_OPTIONS.length - 1}
        onDecrease={() => setGoal(goalIndex - 1)}
        onIncrease={() => setGoal(goalIndex + 1)}
      >
        <div
          style={{
            padding: 8,
            borderRadius: 12,
            backgroundColor: colors.white,
            border: `1px solid ${colors.greyLight}`,
            textAlign: "center",
          }}
        >
          <div className="text-base font-bold" style={{ color: colors.primary }}>
            {goalOption.displayName}
          </div>
          <div className="text-sm" style={{ color: colors.secondary }}>
            {goalOption.description}
          </div>
        </div>
      </Stepper>

      <SectionDivider />

      {/* Video Topics */}
      <button
        type="button"
        onClick={() => setTopicsOpen(true)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <span className="text-base" style={{ color: colors.primary }}>Video Topics</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="text-sm" style={{ color: colors.muted }}>
            {selectedTopicCount > 0 ? `${selectedTopicCount} selected` : "All topics"}
          </span>
          <span className="text-lg" style={{ color: colors.muted }}>›</span>
        </span>
      </button>

      <FeedTopicsModal open={topicsOpen} onClose={() => setTopicsOpen(false)} />
    </div>
  );
};

export default FeedSettingsSection;
